import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useToast, toastErrorMessage } from "../lib/toast";
import { fmtAestDateTime, todayAestISO } from "../lib/format";

export default function QuoteEdit() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { user, meta } = useAuth();
  const toast = useToast();
  const presetCompany = sp.get("company_id");
  const isNew = !id;
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [f, setF] = useState({
    company_id: presetCompany || "",
    quote_number:"", product_segment:"D", product_category:"",
    item_description:"", date_of_quote: todayAestISO(),
    reminder_date:"", date_expected_purchase:"", value_of_sale:0,
    special_information:"", state:"", status:"OPEN",
    loss_reason:"", loss_reason_text:"",
    won_reason:"", won_reason_text:"",
  });
  const [repId, setRepId] = useState("");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [audit, setAudit] = useState([]);

  useEffect(() => {
    api.get("/companies?limit=500").then(r => setCompanies(r.data)).catch(() => {});
    // Only admins can reassign - only fetch the user list for them.
    if (user?.role === "admin") {
      api.get("/users").then(r => setUsers(r.data)).catch(() => {});
    }
  }, [user?.role]);

  useEffect(() => {
    if (isNew) return;
    api.get(`/quotes/${id}`).then(r => {
      setF({ ...r.data });
      setRepId(r.data.created_by_id || "");
    }).catch(() => {});
  }, [id, isNew]);

  async function save() {
    setSaving(true); setErr("");
    try {
      const payload = { ...f, value_of_sale: Math.max(0, Math.round(Number(f.value_of_sale || 0))) };
      // Trim and let the server allocate if the user left it blank on create.
      payload.quote_number = (payload.quote_number || "").trim();
      if (!["DRAFTING","OPEN","WON","LOST"].includes(payload.status)) payload.status = "OPEN";
      // Admin-only rep reassignment - only send when the value actually changed.
      if (!isNew && user?.role === "admin" && repId && repId !== f.created_by_id) {
        payload.rep_id = repId;
      }
      if (!isNew) {
        for (const k of Object.keys(payload)) if (payload[k] === "") payload[k] = null;
        await api.patch(`/quotes/${id}`, payload);
        // Keep local created_by_id in sync so a second Save-without-refresh
        // doesn't re-send the same rep_id and log a duplicate audit event.
        if (payload.rep_id) setF(prev => ({ ...prev, created_by_id: payload.rep_id }));
        toast.success(`Quote ${payload.quote_number || f.quote_number} saved`);
      } else {
        const { data } = await api.post("/quotes", payload);
        toast.success(`Quote ${data.quote_number} created`);
        nav(`/quotes/${data.id}`, { replace: true });
        return;
      }
    } catch(e) {
      const msg = toastErrorMessage(e);
      setErr(msg); toast.error(msg);
    } finally { setSaving(false); }
  }

  const cats = meta?.quote_product_categories?.[f.product_segment] || [];
  return (
    <div className="space-y-4 max-w-3xl" data-testid="quote-edit-page">
      <div>
        <Link to="/quotes" className="text-sm text-gray-500 hover:text-black">← Quotes</Link>
        <h1 className="text-2xl font-bold">{isNew ? "New quote" : `Quote ${f.quote_number}`}</h1>
      </div>
      <div className="cesco-card p-5 grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="cesco-label">Company *</label>
          <select className="cesco-input" value={f.company_id} required disabled={!isNew}
                  onChange={e=>setF({...f, company_id:e.target.value})} data-testid="quote-company">
            <option value="">Select company</option>
            {companies.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
          </select>
        </div>
        <div>
          <label className="cesco-label">Quote number{isNew ? "" : " *"}</label>
          <input className="cesco-input" value={f.quote_number || ""}
                 placeholder={isNew ? "Auto-assigned (CE-1000, CE-1001…) - leave blank" : ""}
                 onChange={e=>setF({...f, quote_number:e.target.value})} data-testid="quote-number"/>
          {isNew && <div className="text-xs text-gray-500 mt-1">Leave blank to auto-assign the next number.</div>}
        </div>
        <div>
          <label className="cesco-label">Status</label>
          <select className="cesco-input" value={f.status}
                  onChange={e=>setF({...f, status:e.target.value})} data-testid="quote-status">
            <option value="DRAFTING">DRAFTING</option>
            <option value="OPEN">OPEN</option>
            <option value="WON">WON</option>
            <option value="LOST">LOST</option>
          </select>
        </div>
        {!isNew && user?.role === "admin" && (
          <div>
            <label className="cesco-label">Rep</label>
            <select className="cesco-input" value={repId}
                    onChange={e=>setRepId(e.target.value)} data-testid="quote-rep">
              <option value="">- (unassigned)</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
            </select>
            <div className="text-[11px] text-gray-500 mt-1">
              Admin-only. Reassigning moves the quote onto the selected rep&apos;s dashboard.
            </div>
          </div>
        )}
        <div>
          <label className="cesco-label">Product segment</label>
          <select className="cesco-input" value={f.product_segment}
                  onChange={e=>setF({...f, product_segment:e.target.value, product_category:""})}
                  data-testid="quote-segment">
            <option value="D">Drill</option><option value="M">Mixer</option>
          </select>
        </div>
        <div>
          <label className="cesco-label">Product category</label>
          <select className="cesco-input" value={f.product_category}
                  onChange={e=>setF({...f, product_category:e.target.value})} data-testid="quote-category">
            <option value="">-</option>
            {cats.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="col-span-2">
          <label className="cesco-label">Short description</label>
          <input className="cesco-input" maxLength={20} value={f.item_description || ""}
                 onChange={e=>setF({...f, item_description:e.target.value})}
                 data-testid="quote-short-description"/>
          <div className="text-[11px] text-gray-400 mt-1">{(f.item_description || "").length}/20</div>
        </div>
        <div>
          <label className="cesco-label">Date of quote</label>
          <input className="cesco-input" type="date" value={f.date_of_quote || ""}
                 onChange={e=>setF({...f, date_of_quote:e.target.value})}/>
        </div>
        <div>
          <label className="cesco-label">Value of quote (AUD)</label>
          <input className="cesco-input" type="number" step="1" min="0" value={f.value_of_sale}
                 onChange={e=>setF({...f, value_of_sale:e.target.value})} data-testid="quote-value"/>
        </div>
        <div>
          <label className="cesco-label">Reminder date</label>
          <input className="cesco-input" type="date" value={f.reminder_date || ""}
                 onChange={e=>setF({...f, reminder_date:e.target.value})}/>
        </div>
        <div>
          <label className="cesco-label">Date expected purchase</label>
          <input className="cesco-input" type="date" value={f.date_expected_purchase || ""}
                 onChange={e=>setF({...f, date_expected_purchase:e.target.value})}/>
        </div>
        <div>
          <label className="cesco-label">State</label>
          <select className="cesco-input" value={f.state || ""} onChange={e=>setF({...f, state:e.target.value})}>
            <option value="">-</option>
            {meta?.au_states.map(s => <option key={s.code} value={s.code}>{s.code}</option>)}
          </select>
        </div>
        <div className="col-span-2">
          <label className="cesco-label">Long description</label>
          <textarea className="cesco-input" rows={2} value={f.special_information||""}
                    onChange={e=>setF({...f, special_information:e.target.value})}
                    data-testid="quote-long-description"/>
        </div>

        {f.status === "LOST" && (
          <>
            <div>
              <label className="cesco-label">Loss reason *</label>
              <select className="cesco-input" value={f.loss_reason||""}
                      onChange={e=>setF({...f, loss_reason:e.target.value})} data-testid="quote-loss-reason">
                <option value="">-</option>
                {meta?.loss_reasons.map(r => <option key={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="cesco-label">Loss reason detail *</label>
              <input className="cesco-input" value={f.loss_reason_text||""}
                     onChange={e=>setF({...f, loss_reason_text:e.target.value})} data-testid="quote-loss-text"/>
            </div>
          </>
        )}
        {f.status === "WON" && (
          <>
            <div>
              <label className="cesco-label">Won reason *</label>
              <select className="cesco-input" value={f.won_reason||""}
                      onChange={e=>setF({...f, won_reason:e.target.value})} data-testid="quote-won-reason">
                <option value="">-</option>
                {meta?.won_reasons.map(r => <option key={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="cesco-label">Won reason detail *</label>
              <input className="cesco-input" value={f.won_reason_text||""}
                     onChange={e=>setF({...f, won_reason_text:e.target.value})} data-testid="quote-won-text"/>
            </div>
          </>
        )}

        {err && <div className="col-span-2 text-sm text-red-600" data-testid="quote-error">{err}</div>}
        <div className="col-span-2 flex gap-2 justify-end">
          <button className="cesco-btn-ghost" onClick={()=>nav(-1)}>Cancel</button>
          <button className="cesco-btn-primary" disabled={saving} onClick={save}
                  data-testid="quote-save">{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}
